// ipd-due.js — the PURE due-engine for IPD v2 (the in-patient ward product).
//
// Everything recurring on a ward — a scheduled drug dose, a q4h vitals round, a
// per-shift nursing assessment — is modelled as a DUE ITEM:
//   { kind, slotKey, dueAt, status, ref }
// The slotKey is the idempotency key: a stable string naming ONE expected
// occurrence (e.g. this dose of this order at this local time on this date). A
// completion resource (a MedicationAdministration, a scheduled-round Observation,
// a per-shift QuestionnaireResponse) carries that SAME slotKey in an extension,
// so matching a due slot to its completion is EXACT — never fuzzy time-matching.
//
// This module is deliberately I/O-FREE and Medplum-FREE: it takes plain resource
// arrays + a `now` and returns plain objects. That makes it deterministic and
// unit-testable with `node --test` (tests/ipd2/due-engine.test.mjs). All the
// Medplum reads/writes live in ipd-live.js, which feeds this engine its inputs.
//
// Contract: docs/ipd/IPD_V2_DATA_CONTRACT.md §1 (due-engine) + §3 (signatures).

// ── Namespaced FHIR extension URLs (contract §1) ────────────────────────────
export const SLOT_KEY_EXT = 'https://healther.io/fhir/ipd/slot-key';   // valueString = slotKey
export const SHIFT_EXT = 'https://healther.io/fhir/ipd/shift';         // valueCode = 'M' | 'E' | 'N'
export const BRADEN_SCORE_EXT = 'https://healther.io/fhir/ipd/braden-score'; // valueInteger (on the QR)
export const MORSE_SCORE_EXT = 'https://healther.io/fhir/ipd/morse-score';   // valueInteger (on the QR)

// ── Shift windows (v1 defaults; config later, contract §1) ──────────────────
//   M (morning)  07:00–14:00
//   E (evening)  14:00–21:00
//   N (night)    21:00–07:00  (wraps midnight; the 00:00–07:00 half belongs to
//                the PREVIOUS calendar date's N shift — see shiftFor).
const SHIFT_M_START = 7;   // 07:00
const SHIFT_E_START = 14;  // 14:00
const SHIFT_N_START = 21;  // 21:00

// ── Grace windows before a due item flips to 'overdue' (contract §1) ────────
const DOSE_GRACE_MIN = 60;         // dose overdue > dueAt + 60m
const ASSESS_GRACE_MIN = 120;      // assessment overdue > dueAt + 120m
const VITALS_GRACE_CAP_MIN = 60;   // vitals grace = period/2, capped at 60m

// ── FREQ enum → default timeOfDay grid (contract §2, from IPD 1.9) ──────────
// The order stores its EXPLICIT timeOfDay at creation so the engine never
// re-derives; this table is the fallback the writer uses to fill that in, and
// the engine reuses it only when an order somehow carries a FREQ but no explicit
// timeOfDay (defensive, so a legacy order still expands).
export const FREQ_TIMES = {
  OD:  ['09:00'],
  BD:  ['09:00', '21:00'],
  TDS: ['09:00', '14:00', '21:00'],
  QID: ['06:00', '12:00', '18:00', '22:00'],
  HS:  ['21:00'],
  Q4H:  hourGrid(4),
  Q6H:  hourGrid(6),
  Q8H:  hourGrid(8),
  Q12H: hourGrid(12),
  // PRN / STAT are NOT scheduled grids — handled specially below.
  PRN:  [],
  STAT: [],
};

// A QnH grid anchored at 06:00 local (contract §2: "QnH → grid from 06:00").
function hourGrid(everyHours) {
  const out = [];
  for (let h = 6; h < 6 + 24; h += everyHours) out.push(hhmm(h % 24, 0));
  // Dedupe + sort so a grid that wraps past 24 (e.g. Q4H → …22:00,02:00) is
  // presented in clock order; the date those belong to is resolved per-slot.
  return [...new Set(out)].sort();
}

function pad2(n) { return String(n).padStart(2, '0'); }
function hhmm(h, m) { return `${pad2(h)}:${pad2(m)}`; }

// A local-time ISO instant for {YYYY-MM-DD} + {HH:mm} with NO timezone suffix,
// interpreted in the runtime's local zone (the ward's clock). We build a Date
// from the numeric parts (local constructor) so DST/zone handling matches the
// nurse's wall clock — the same clock the paper form's shift columns assume.
function localInstant(dateStr, hhmmStr) {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = hhmmStr.split(':').map(Number);
  return new Date(y, mo - 1, d, h, mi, 0, 0);
}

// 'YYYY-MM-DD' for a Date in LOCAL time (not toISOString, which is UTC).
export function localDateStr(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

// Read the slotKey off a completion resource's extension[] (contract §1).
export function slotKeyOf(resource) {
  const ext = (resource && resource.extension) || [];
  const hit = ext.find((e) => e.url === SLOT_KEY_EXT);
  return (hit && typeof hit.valueString === 'string') ? hit.valueString : null;
}

// ── shiftFor(date) → { shift, date, windowStart, windowEnd } ────────────────
// Which nursing shift owns this instant, and its window. The N shift wraps
// midnight, so an instant at 00:00–06:59 belongs to the PREVIOUS calendar date's
// N shift (its window started at 21:00 the day before). Boundaries (contract):
//   21:00 → N (of that date) · 07:00 → M · 06:59 → N (of the PREVIOUS date).
export function shiftFor(date) {
  const d = (date instanceof Date) ? date : new Date(date);
  const h = d.getHours();
  if (h >= SHIFT_M_START && h < SHIFT_E_START) {
    // Morning 07:00–14:00
    const ds = localDateStr(d);
    return { shift: 'M', date: ds, windowStart: localInstant(ds, '07:00'), windowEnd: localInstant(ds, '14:00') };
  }
  if (h >= SHIFT_E_START && h < SHIFT_N_START) {
    // Evening 14:00–21:00
    const ds = localDateStr(d);
    return { shift: 'E', date: ds, windowStart: localInstant(ds, '14:00'), windowEnd: localInstant(ds, '21:00') };
  }
  // Night 21:00–07:00 (wraps). If the instant is at/after 21:00 it belongs to
  // THIS date's N shift; if it's before 07:00 it belongs to the PREVIOUS date's
  // N shift (the window opened at 21:00 yesterday).
  if (h >= SHIFT_N_START) {
    const ds = localDateStr(d);
    const next = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    return { shift: 'N', date: ds, windowStart: localInstant(ds, '21:00'), windowEnd: localInstant(localDateStr(next), '07:00') };
  }
  // h < 07:00 → previous date's N shift.
  const prev = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
  const pds = localDateStr(prev);
  const ds = localDateStr(d);
  return { shift: 'N', date: pds, windowStart: localInstant(pds, '21:00'), windowEnd: localInstant(ds, '07:00') };
}

// ── order helpers ───────────────────────────────────────────────────────────
// The FREQ code lives on timing.code.text (contract §2). Fall back through the
// human dosage text so a hand-written order still classifies.
function freqOf(order) {
  const di = (order.dosageInstruction && order.dosageInstruction[0]) || {};
  const code = di.timing && di.timing.code && di.timing.code.text;
  return code ? String(code).toUpperCase() : '';
}
function timeOfDayOf(order) {
  const di = (order.dosageInstruction && order.dosageInstruction[0]) || {};
  const t = di.timing && di.timing.repeat && di.timing.repeat.timeOfDay;
  return Array.isArray(t) ? t.slice() : [];
}
function isPrnOrder(order) {
  const di = (order.dosageInstruction && order.dosageInstruction[0]) || {};
  return di.asNeededBoolean === true || freqOf(order) === 'PRN';
}
function isStatOrder(order) {
  return freqOf(order) === 'STAT';
}
// An order is "live" for scheduling only while status is 'active' (a 'stopped'
// order stops generating new slots; already-recorded ticks still show).
function isActiveOrder(order) {
  return !order.status || order.status === 'active';
}

// ── computeDoseSlots(order, forDate) → [{ slotKey, dueAt }] ──────────────────
// Expand ONE MedicationRequest into its expected dose slots for the given local
// calendar date. Uses the order's EXPLICIT timeOfDay (contract §2: stored at
// creation) and falls back to the FREQ grid only if absent. PRN yields no
// scheduled slots (always-available action, rendered by the UI, never "due").
// STAT yields exactly ONE immediate slot at the order's authored time on that
// date (a single dose to give now), keyed like any other slot for idempotency.
export function computeDoseSlots(order, forDate) {
  if (!order || !order.id) return [];
  const dateStr = typeof forDate === 'string' ? forDate : localDateStr(forDate);

  if (isPrnOrder(order)) return []; // PRN is on-demand, never a scheduled slot.

  if (isStatOrder(order)) {
    // One immediate slot. Anchor at the order's authoredOn time-of-day when it
    // falls on this date; else at 00:00 so it reads as "due now" the moment the
    // order exists. STAT is a single administration — one slotKey.
    const authored = order.authoredOn ? new Date(order.authoredOn) : null;
    const hm = (authored && localDateStr(authored) === dateStr) ? hhmm(authored.getHours(), authored.getMinutes()) : '00:00';
    return [{ slotKey: doseSlotKey(order.id, dateStr, hm), dueAt: localInstant(dateStr, hm).toISOString() }];
  }

  let times = timeOfDayOf(order);
  if (!times.length) {
    const grid = FREQ_TIMES[freqOf(order)];
    times = Array.isArray(grid) ? grid : [];
  }
  // Normalise to HH:mm, dedupe, clock-order — stable slotKeys regardless of the
  // order the times were stored in.
  const norm = [...new Set(times.map((t) => String(t).slice(0, 5)))].sort();
  return norm.map((hm) => ({
    slotKey: doseSlotKey(order.id, dateStr, hm),
    dueAt: localInstant(dateStr, hm).toISOString(),
  }));
}

// slotKey builders (contract §1 formats) ────────────────────────────────────
export function doseSlotKey(orderId, dateStr, hm) {
  return `dose:${orderId}:${dateStr}T${hm}`;
}
export function vitalsSlotKey(encounterId, dateStr, hm) {
  return `vitals:${encounterId}:${dateStr}T${hm}`;
}
export function assessSlotKey(encounterId, dateStr, shift) {
  return `assess:${encounterId}:${dateStr}:${shift}`;
}

// Vitals slots for a date given the every-N-hours period, anchored at 06:00
// local (contract §2). Returns [{ slotKey, dueAt, everyHours }].
function computeVitalsSlots(encounterId, everyHours, dateStr) {
  const n = Number(everyHours);
  if (!Number.isFinite(n) || n <= 0) return [];
  const out = [];
  for (let h = 6; h < 6 + 24; h += n) {
    const hh = h % 24;
    const hm = hhmm(hh, 0);
    out.push({ slotKey: vitalsSlotKey(encounterId, dateStr, hm), dueAt: localInstant(dateStr, hm).toISOString(), everyHours: n });
  }
  // Dedupe by slotKey (a 24-divisor period can revisit 06:00) preserving order.
  const seen = new Set();
  return out.filter((s) => (seen.has(s.slotKey) ? false : (seen.add(s.slotKey), true)));
}

// The three assessment slots for a date (one per shift), contract §1.
function computeAssessmentSlots(encounterId, dateStr) {
  return ['M', 'E', 'N'].map((shift) => ({
    slotKey: assessSlotKey(encounterId, dateStr, shift),
    shift,
    // The assessment is "due" from the start of its shift window.
    dueAt: localInstant(dateStr, shift === 'M' ? '07:00' : shift === 'E' ? '14:00' : '21:00').toISOString(),
  }));
}

// ── Worklist window (product decision, 2026-07-07) ──────────────────────────
// The cross-patient nurse worklist shows only the "what needs doing NOW" band:
// a due/overdue slot whose dueAt is within [now − 24h, now + 2h]. Older
// incomplete slots (a multi-day backlog the engine still expands so the cardex
// can show per-day history) are NOT worklist rows — they were the "88 overdue /
// 2D OVERDUE" noise. This is a PURE predicate so listWorklist (and the WardBoard
// overdue count) filter identically and it is unit-testable. PRN action items
// (dueAt null) always pass — they are always-available, never "backlog".
const WORKLIST_PAST_MS = 24 * 60 * 60000; // 24h behind
const WORKLIST_AHEAD_MS = 2 * 60 * 60000;  // 2h ahead

export function withinWorklistWindow(dueItem, now = new Date()) {
  if (!dueItem) return false;
  if (dueItem.prn || dueItem.dueAt == null) return true; // always-available action
  const t = new Date(dueItem.dueAt).getTime();
  if (!Number.isFinite(t)) return false;
  const nowMs = (now instanceof Date ? now : new Date(now)).getTime();
  return t >= nowMs - WORKLIST_PAST_MS && t <= nowMs + WORKLIST_AHEAD_MS;
}

// ── collapseMissedRounds(items) → items (wave 2, contract §7a) ───────────────
// A recurring round (vitals q2h, per-shift assessment) missed several times
// reads as ONE worklist row — "vitals overdue (3 rounds missed)" — not three
// near-identical lines. A missed DOSE is clinically distinct per occurrence
// (each slot is one specific drug administration that did or did not happen),
// so doses NEVER collapse.
//
// Pure + non-mutating. For each (encounterId, kind) group of OVERDUE recurring
// items (kind 'vitals' | 'assessment'), only the MOST RECENT occurrence (max
// dueAt) survives, annotated with `missedCount` = how many overdue occurrences
// it stands for (including itself). Everything else — doses, due/upcoming/
// done/held items — passes through untouched, in input order. The encounterId
// is read off the item (worklist items carry it) or parsed from the slotKey
// (`vitals:{encId}:…` / `assess:{encId}:…`) so the helper also works on bare
// computeDueItems output.
export function collapseMissedRounds(items) {
  const list = Array.isArray(items) ? items : [];
  const collapses = (it) => it && (it.kind === 'vitals' || it.kind === 'assessment') && it.status === 'overdue';
  const groupKeyOf = (it) => {
    const enc = it.encounterId
      || (/^(?:vitals|assess):([^:]+):/.exec(String(it.slotKey || '')) || [])[1]
      || '';
    return `${enc}|${it.kind}`;
  };
  // One pass to find, per group, the winning (latest-due) index + the count.
  const groups = new Map(); // groupKey → { count, latestIdx, latestT }
  list.forEach((it, idx) => {
    if (!collapses(it)) return;
    const t = new Date(it.dueAt || 0).getTime();
    const g = groups.get(groupKeyOf(it));
    if (!g) { groups.set(groupKeyOf(it), { count: 1, latestIdx: idx, latestT: t }); return; }
    g.count += 1;
    if (t > g.latestT) { g.latestT = t; g.latestIdx = idx; }
  });
  const out = [];
  list.forEach((it, idx) => {
    if (!collapses(it)) { out.push(it); return; }
    const g = groups.get(groupKeyOf(it));
    if (idx !== g.latestIdx) return; // collapsed into the survivor
    out.push({ ...it, missedCount: g.count });
  });
  return out;
}

// status of a due slot given now + its completion (contract §1) ─────────────
function statusFor({ dueAt, graceMinutes, completion, now }) {
  if (completion) {
    // A completion resource with this slotKey exists → done or held. A dose
    // MedicationAdministration status:'not-done' (held/refused/unavailable) is
    // 'held'; anything else that carries the slotKey counts as 'done'.
    return completion.status === 'not-done' ? 'held' : 'done';
  }
  const due = new Date(dueAt).getTime();
  const t = now.getTime();
  if (t > due + graceMinutes * 60000) return 'overdue';
  if (t >= due) return 'due';
  return 'upcoming';
}

// ── computeDueItems(...) → dueItem[] (contract §3) ──────────────────────────
// The master roll-up for ONE encounter across a window of dates (yesterday +
// today by default, so the tail of a night shift is covered). Produces every
// expected dose / vitals / assessment slot, matched to its completion by
// slotKey, each stamped with status. PRN orders contribute an always-available
// action item (kind 'dose', status 'upcoming', prn:true) with no scheduled time.
//
// inputs (all plain resources/arrays):
//   orders          MedicationRequest[]  (active + stopped; stopped stop new slots)
//   administrations MedicationAdministration[]  (carry slotKey ext)
//   vitalsSchedule  ServiceRequest | null  (occurrenceTiming.repeat.period[Unit])
//   observations    Observation[]  (scheduled-round vitals carry slotKey ext)
//   assessments     QuestionnaireResponse[]  (carry slotKey ext)
//   encounter       Encounter  (its id keys vitals/assessment slots)
//   now             Date  (deterministic — the caller passes it)
//   dates           string[]  optional explicit local dates; default [yesterday, today]
// The default date window computeDueItems expands slots over when the caller
// gives no explicit `dates`. Base window is [yesterday, today] (so the tail of a
// night shift stays covered). BUT close to midnight that hides the next few
// post-midnight slots — a 00:00/02:00 dose or vitals round tonight isn't
// generated until the calendar date actually rolls over, so it silently vanishes
// from "next due" for the last couple of hours of the day. When `now` is within
// ~2h of midnight (>= 22:00 local) we also include TOMORROW, so those upcoming
// post-midnight occurrences compute as 'upcoming' right away. Pure (derives only
// from nowD); nothing downstream promotes an 'upcoming' slot to actionable (the
// worklist/Due-tab windows still gate on due/overdue), so this only makes the
// imminent slot visible early, never fires it early.
function defaultDateWindow(nowD) {
  const y = nowD.getFullYear();
  const mo = nowD.getMonth();
  const d = nowD.getDate();
  const win = [
    localDateStr(new Date(y, mo, d - 1)),
    localDateStr(new Date(y, mo, d)),
  ];
  if (nowD.getHours() >= 22) win.push(localDateStr(new Date(y, mo, d + 1)));
  return win;
}

export function computeDueItems({ orders = [], administrations = [], vitalsSchedule = null, observations = [], assessments = [], encounter = null, now = new Date(), dates } = {}) {
  const nowD = (now instanceof Date) ? now : new Date(now);
  const encId = encounter && encounter.id;
  const dateList = Array.isArray(dates) && dates.length
    ? dates
    : defaultDateWindow(nowD);

  // Nothing is due BEFORE the admission existed: slots earlier than
  // Encounter.period.start are skipped (a patient admitted at 23:00 must not
  // open with the day's earlier vitals rounds/doses "overdue"). A completed
  // slot is kept regardless (a recorded tick is part of the record).
  const admitMs = (() => {
    // Tolerate BOTH the raw FHIR Encounter (period.start) and the flat
    // projection ipd-live's listWorklist passes ({ periodStart, resource }).
    const s = (encounter && encounter.period && encounter.period.start)
      || (encounter && encounter.periodStart)
      || (encounter && encounter.resource && encounter.resource.period && encounter.resource.period.start);
    const t = s ? new Date(s).getTime() : NaN;
    return Number.isFinite(t) ? t : null;
  })();
  const beforeAdmission = (dueAtIso) => admitMs != null && new Date(dueAtIso).getTime() < admitMs;

  // Index completions by slotKey once (O(n)); a slot looks up its completion in O(1).
  const adminBySlot = indexBySlot(administrations);
  const obsBySlot = indexBySlot(observations);
  const assessBySlot = indexBySlot(assessments);

  const items = [];

  // ── doses ──
  for (const order of orders) {
    if (isPrnOrder(order)) {
      // Always-available PRN action — no dueAt, never overdue. Only for a live
      // order (a stopped PRN drops off the board).
      if (isActiveOrder(order)) {
        items.push({ kind: 'dose', slotKey: `prn:${order.id}`, dueAt: null, status: 'upcoming', prn: true, ref: refOf(order) });
      }
      continue;
    }
    if (!isActiveOrder(order) && !isStatOrder(order)) {
      // A stopped scheduled order generates no NEW slots, but we still surface
      // slots that already have a recorded administration (so the shift's record
      // is complete). Handled by only pushing slots that have a completion below.
    }
    for (const dateStr of dateList) {
      for (const slot of computeDoseSlots(order, dateStr)) {
        const completion = adminBySlot.get(slot.slotKey) || null;
        if (!isActiveOrder(order) && !completion) continue; // stopped + never given → skip
        if (beforeAdmission(slot.dueAt) && !completion) continue; // pre-admission slot → not expected
        items.push({
          kind: 'dose', slotKey: slot.slotKey, dueAt: slot.dueAt,
          status: statusFor({ dueAt: slot.dueAt, graceMinutes: DOSE_GRACE_MIN, completion, now: nowD }),
          ref: refOf(order), completion: completion ? refOf(completion) : null,
        });
      }
    }
  }

  // ── vitals ──
  if (encId && vitalsSchedule) {
    const everyHours = periodHoursOf(vitalsSchedule);
    if (everyHours) {
      const graceMin = Math.min(VITALS_GRACE_CAP_MIN, (everyHours * 60) / 2);
      for (const dateStr of dateList) {
        for (const slot of computeVitalsSlots(encId, everyHours, dateStr)) {
          const completion = obsBySlot.get(slot.slotKey) || null;
          if (beforeAdmission(slot.dueAt) && !completion) continue; // pre-admission slot
          items.push({
            kind: 'vitals', slotKey: slot.slotKey, dueAt: slot.dueAt,
            status: statusFor({ dueAt: slot.dueAt, graceMinutes: graceMin, completion, now: nowD }),
            ref: refOf(vitalsSchedule), completion: completion ? refOf(completion) : null,
          });
        }
      }
    }
  }

  // ── assessments (one per shift per day) ──
  if (encId) {
    for (const dateStr of dateList) {
      for (const slot of computeAssessmentSlots(encId, dateStr)) {
        const completion = assessBySlot.get(slot.slotKey) || null;
        // Pre-admission rule for assessments is per SHIFT WINDOW, not per dueAt:
        // a patient admitted mid-shift still owes THAT shift's assessment (its
        // dueAt = the shift start, which predates the admission). Only a shift
        // that fully ENDED before admission is skipped.
        if (admitMs != null && !completion) {
          const windowEnd = slot.shift === 'M' ? localInstant(dateStr, '14:00')
            : slot.shift === 'E' ? localInstant(dateStr, '21:00')
            : localInstant(localDateStr(new Date(new Date(dateStr + 'T00:00:00').getTime() + 86400000)), '07:00');
          if (windowEnd.getTime() <= admitMs) continue;
        }
        items.push({
          kind: 'assessment', slotKey: slot.slotKey, dueAt: slot.dueAt, shift: slot.shift,
          status: statusFor({ dueAt: slot.dueAt, graceMinutes: ASSESS_GRACE_MIN, completion, now: nowD }),
          ref: encId ? { reference: `Encounter/${encId}` } : null,
          completion: completion ? refOf(completion) : null,
        });
      }
    }
  }

  return items;
}

function indexBySlot(resources) {
  const m = new Map();
  for (const r of resources || []) {
    const k = slotKeyOf(r);
    // First writer wins per slotKey; a duplicate completion (re-tick) is ignored
    // for status purposes — the slot is already done/held.
    if (k && !m.has(k)) m.set(k, r);
  }
  return m;
}

function refOf(resource) {
  return resource && resource.resourceType && resource.id
    ? { reference: `${resource.resourceType}/${resource.id}` }
    : null;
}

// ServiceRequest.occurrenceTiming.repeat.period(+periodUnit 'h') → hours.
function periodHoursOf(serviceRequest) {
  const rep = serviceRequest && serviceRequest.occurrenceTiming && serviceRequest.occurrenceTiming.repeat;
  if (!rep) return null;
  const period = Number(rep.period);
  if (!Number.isFinite(period) || period <= 0) return null;
  const unit = rep.periodUnit || 'h';
  if (unit === 'h') return period;
  if (unit === 'd') return period * 24;
  if (unit === 'min') return period / 60;
  return period; // assume hours if an unexpected unit slips through.
}

// ── composeHandoverDigest(...) → plain object (contract §1/§3) ──────────────
// Auto-compose the shift's handover digest from the ticked data — the nurse then
// edits/confirms it (never free-composed from memory, contract §2). Returns a
// plain object the UI renders into editable sections + can serialise to the
// Composition's XHTML. Scoped to ONE shift window (the caller passes the shift's
// resources, or we filter by the shift/date if given).
export function composeHandoverDigest({ shift, date, administrations = [], observations = [], assessments = [], orders = [] } = {}) {
  const orderById = new Map((orders || []).map((o) => [o.id, o]));
  // Tolerant of BOTH raw MedicationRequest (medicationCodeableConcept.text) and
  // the flat order projection ipd-live emits (.name) — the handover tab passes
  // whatever getBedContext returned (projections), the unit test passes raw.
  const nameOfOrder = (o) => (o && (o.name || (o.medicationCodeableConcept && o.medicationCodeableConcept.text))) || null;
  const drugName = (admin) => {
    if (admin.medicationCodeableConcept && admin.medicationCodeableConcept.text) return admin.medicationCodeableConcept.text;
    const reqRef = admin.request && admin.request.reference;
    const oid = reqRef ? reqRef.split('/')[1] : null;
    return (oid && nameOfOrder(orderById.get(oid))) || 'medication';
  };

  const given = [];
  const held = [];
  for (const a of administrations || []) {
    const line = {
      slotKey: slotKeyOf(a),
      drug: drugName(a),
      dose: (a.dosage && a.dosage.text) || null,
      at: a.effectiveDateTime || null,
      reason: (a.statusReason && a.statusReason[0] && a.statusReason[0].text) || null,
    };
    (a.status === 'not-done' ? held : given).push(line);
  }

  // Vitals summary — the latest reading of each kind in the window.
  const vitals = summariseVitals(observations);

  // Assessment flags — Braden/Morse totals (read off the QR extension when present)
  // + which shift each was filed for.
  const assessment = [];
  for (const qr of assessments || []) {
    assessment.push({
      slotKey: slotKeyOf(qr),
      shift: shiftCodeOf(qr),
      authored: qr.authored || null,
      braden: extInt(qr, BRADEN_SCORE_EXT),
      morse: extInt(qr, MORSE_SCORE_EXT),
    });
  }

  // Return the SECTION keys Agent C's handover tab maps 1:1 (contract §1):
  // given · held · vitals · assessment · events · watchFor. `events`/`watchFor`
  // are nurse-authored free text (nothing to auto-derive), seeded empty so the
  // editor renders the labelled sections. Legacy fields kept for back-compat.
  return {
    shift: shift || null,
    date: date || null,
    given,
    held,
    vitals,
    assessment,
    events: [],     // shift events — nurse-authored (no ticked source to derive)
    watchFor: '',   // hand-off "watch for" note — nurse-authored free text
    // convenience counts + a seed summary line the editor can prefill.
    givenCount: given.length,
    heldCount: held.length,
    summary: buildSummaryLine({ given, held, vitals, assessFlags: assessment }),
    // legacy grouping kept so nothing that read the old shape breaks.
    doses: { given, held, givenCount: given.length, heldCount: held.length },
    assessments: assessment,
  };
}

// Read a valueInteger from a resource's extension[] by url (Braden/Morse on QR).
function extInt(resource, url) {
  const hit = ((resource && resource.extension) || []).find((e) => e.url === url);
  return hit && typeof hit.valueInteger === 'number' ? hit.valueInteger : null;
}

function shiftCodeOf(resource) {
  const hit = ((resource && resource.extension) || []).find((e) => e.url === SHIFT_EXT);
  return (hit && hit.valueCode) || null;
}

// Latest value of each vital kind across the window's Observations. Reads the
// same LOINC codes OPD writes (server/opd/vitals.mjs) so the digest speaks the
// one vitals stream: BP panel 85354-9 (components 8480-6/8462-4), HR 8867-4,
// SpO2 59408-5, temp 8310-5, RR 9279-1.
function summariseVitals(observations) {
  const byKind = {};
  const sorted = [...(observations || [])].sort((a, b) =>
    String(b.effectiveDateTime || '').localeCompare(String(a.effectiveDateTime || '')));
  const compVal = (obs, code) => (obs.component || [])
    .find((c) => (c.code && c.code.coding || []).some((cc) => cc.code === code));
  for (const o of sorted) {
    const codes = (o.code && o.code.coding || []).map((c) => c.code);
    if (codes.includes('85354-9') && byKind.bp === undefined) {
      const sys = compVal(o, '8480-6');
      const dia = compVal(o, '8462-4');
      const s = sys && sys.valueQuantity && sys.valueQuantity.value;
      const d = dia && dia.valueQuantity && dia.valueQuantity.value;
      if (s != null || d != null) byKind.bp = `${s ?? '—'}/${d ?? '—'}`;
    }
    const single = (code, key) => {
      if (codes.includes(code) && byKind[key] === undefined && o.valueQuantity) byKind[key] = o.valueQuantity.value;
    };
    single('8867-4', 'hr');
    single('59408-5', 'spo2');
    single('8310-5', 'temp');
    single('9279-1', 'rr');
  }
  return byKind;
}

function buildSummaryLine({ given, held, vitals, assessFlags }) {
  const parts = [];
  parts.push(`${given.length} dose${given.length === 1 ? '' : 's'} given`);
  if (held.length) parts.push(`${held.length} held/refused`);
  const v = [];
  if (vitals.bp) v.push(`BP ${vitals.bp}`);
  if (vitals.hr != null) v.push(`HR ${vitals.hr}`);
  if (vitals.spo2 != null) v.push(`SpO2 ${vitals.spo2}%`);
  if (vitals.temp != null) v.push(`T ${vitals.temp}`);
  if (v.length) parts.push('last vitals ' + v.join(' · '));
  if (assessFlags.length) parts.push(`${assessFlags.length} assessment${assessFlags.length === 1 ? '' : 's'} filed`);
  return parts.join('; ') + '.';
}
